import {DatePipe} from '@angular/common';
import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, UntypedFormBuilder, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {nonZeroValidator} from 'src/app/shared/validators/non-zero.validator';
import {EventsApiService} from 'src/app/admin/events.service';
import {minimumArrayLengthValidator} from 'src/app/shared/validators/min-arr-length.validator';
import {ShopProductService} from 'src/app/admin/shop/products/new-product/components/product-instances/product-instances.service';
import {environment} from 'src/environments/environment';
import {PRODUCTSUBTYPESCHOICES} from 'src/app/admin/shop/products/new-product/components/product-details/data/data';
import {TaskManagementApiService} from 'src/app/admin/task-management/task-api.service';
import {LayoutComponent} from 'src/app/admin/layout.component';
import {LayoutAlertService} from 'src/app/admin/layout-alert.service';

@Component({
    selector: 'app-create-products',
    templateUrl: './create-products.component.html',
    styleUrls: ['./create-products.component.scss'],
    providers: [DatePipe, LayoutComponent, ShopProductService, TaskManagementApiService, EventsApiService],
    standalone: false
})
export class CreateProductsComponent implements OnInit {
  isSubmitting = false;
  popupTitle = 'Create products';
  form: FormGroup;
  existingProducts: any = [];
  productSubtypesChoices = PRODUCTSUBTYPESCHOICES;
  eventDays;
  duplicatedWeekdays = [];
  productsSealedByDefault = ['kpn', 'discounted_product', 'fully_discounted_product', 'wingame_product'];
  deployUrl;
  taskManagement = false;
  createFromWingame = false;
  showCreatedProducts = false;
  pricingRules: PricingRules;

  @ViewChild('productsContainer', {static: false})
  productsContainer!: ElementRef<HTMLDivElement>;

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<CreateProductsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private datePipe: DatePipe,
    private eventsApiService: EventsApiService,
    private shopProductService: ShopProductService,
    private taskService: TaskManagementApiService,
    private layoutAlertService: LayoutAlertService,
  ) {}

  ngOnInit(): void {
    this.deployUrl = environment.deploy_url;

    const now = new Date();
    const formattedNow = this.datePipe.transform(now, 'yyyy-MM-ddTHH:mm', 'Europe/Amsterdam');

    this.form = this.fb.group({
      relation: [null, Validators.required],
      available_from: [formattedNow, Validators.required],
      products: this.fb.array([this.createProductGroup()], Validators.required),
      is_paid_partner: [null, Validators.requiredTrue],
    });

    this.getPricingRules();

    if (this.data?.wingameData) {
      this.createFromWingame = true;
      this.setDataForWingame(this.data.wingameData);
    } else if (this.data) {
      this.taskManagement = true;
      this.form.controls.is_paid_partner.setValue(true);
      this.prepareForTaskData(this.data);
      this.getExistingProducts(this.data);
    }
  }

  setDataForWingame(wingameData) {
    if (!wingameData.eventId) {
      return;
    }
    this.eventsApiService.getCompanyEvent(wingameData.eventId).subscribe(
      (data: any) => {
        this.relation.setValue({
          city: '',
          company: data?.company ?? null,
          id: data?.id ?? null,
          name: data?.name ?? '',
          start: data?.start ?? '',
          thumbnail: data?.venue?.[0]?.thumbnail ?? '',
          venue_name: data?.venue?.[0]?.name ?? '',
        });
        this.form.controls.is_paid_partner.setValue(data.is_paid_partner);

        this.eventDays = data.days;
        this.getDuplicatedWeekdays();
        this.eventDays = this.eventDays.map(obj => ({
          id: obj.id,
          name: this.getDayName(obj.day).substring(0, 3),
          description: this.getDayName(obj.day),
        }));
        this.checkIfAutoselectDay(this.eventDays);
        this.selectWingameType();
      },
      err => {
        this.layoutAlertService.error('Error when getting event.');
      },
    );
  }

  selectWingameType() {
    this.products.controls.forEach((group: AbstractControl) => {
      this.setWingameSubType(group);
    });
  }

  setWingameSubType(group: AbstractControl) {
    const subTypeCtrl = group.get('product_sub_type');
    if (subTypeCtrl) {
      subTypeCtrl.setValue('wingame_product');
      subTypeCtrl.updateValueAndValidity();
      this.applyPricingRules(group, 'wingame_product');
    }
  }

  getPricingRules() {
    this.shopProductService.getPricingRules().subscribe(
      (data: any) => {
        this.pricingRules = data.data;
        if (this.createFromWingame) {
          this.selectWingameType();
        }
      },
      err => {
        console.error('Getting pricing rules error');
      },
    );
  }

  prepareForTaskData(data) {
    const eventId = data.task.related_event.id;
    if (eventId)
      this.eventsApiService.getEventById(eventId).subscribe((data: any) => {
        this.eventDays = data.days;
        this.getDuplicatedWeekdays();
        this.eventDays = this.eventDays.map(obj => ({
          id: obj.id,
          name: this.getDayName(obj.day).substring(0, 3),
          description: this.getDayName(obj.day),
        }));
        this.checkIfAutoselectDay(this.eventDays);

        const event = {
          city: '',
          company: data?.company ?? null,
          id: data?.id ?? null,
          name: data?.name ?? '',
          start: data?.start ?? '',
          thumbnail: data?.venue?.[0]?.thumbnail ?? '',
          venue_name: data?.venue?.[0]?.name ?? '',
        };

        this.relation.setValue(event);
      });
  }

  getExistingProducts(data) {
    this.taskService.getCustomerProducts(this.data.task.campaign || 0).subscribe(response => {
      this.existingProducts = response['products'];
      this.productsSealedByDefault = response['products_sealed_by_default'];
    });
  }

  toggleCreatedProducts() {
    this.showCreatedProducts = !this.showCreatedProducts;
  }

  private createProductGroup(): FormGroup {
    return this.fb.group({
      title: ['', Validators.required],
      product_sub_type: [null, Validators.required],
      days: [[], minimumArrayLengthValidator(1)],
      customer_upload_min_instances_amount: [1, [Validators.min(1)]],
      is_sealed: [false],
      create_scan_results_task: [false],
      product_title: ['', Validators.required],
      money_amount: [null, [Validators.required, nonZeroValidator()]],
      service_fee: [null, [Validators.required, nonZeroValidator()]],
    });
  }

  addProduct() {
    const group = this.createProductGroup();
    this.products.push(group);
    this.checkIfAutoselectDay(this.eventDays);
    if (this.createFromWingame) {
      this.setWingameSubType(group);
    }
    setTimeout(() => {
      this.scrollToBottom();
    });
  }

  scrollToBottom() {
    const el = this.productsContainer.nativeElement;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth',
    });
  }

  removeProduct(index: number) {
    if (this.products.length > 1) {
      this.products.removeAt(index);
    }
  }

  onItemSelected(value) {
    this.form.get('relation').setValue(value);

    this.eventsApiService.getCompanyEvent(value.id).subscribe(
      (data: any) => {
        this.eventDays = data.days;
        this.getDuplicatedWeekdays();
        this.eventDays = this.eventDays.map(obj => ({
          id: obj.id,
          name: this.getDayName(obj.day).substring(0, 3),
          description: this.getDayName(obj.day),
        }));

        this.checkIfAutoselectDay(this.eventDays);
        this.form.controls.is_paid_partner.setValue(data.is_paid_partner);
      },
      err => {
        console.error('Error when getting event');
      },
    );
  }

  checkIfAutoselectDay(days) {
    if (days?.length === 1) {
      this.products.controls.forEach((group: AbstractControl) => {
        const daysCtrl = group.get('days');
        if (daysCtrl) {
          daysCtrl.setValue([days[0]]);
          daysCtrl.updateValueAndValidity();
        }
      });
    }
  }

  onResetEventId() {
    this.form.get('relation').setValue(null);
    this.clearAllDays();
    this.eventDays = null;
  }

  clearAllDays() {
    this.products.controls.forEach((group: AbstractControl) => {
      const daysCtrl = group.get('days');
      if (daysCtrl) {
        daysCtrl.setValue([]);
        daysCtrl.markAsPristine();
        daysCtrl.markAsUntouched();
        daysCtrl.updateValueAndValidity();
      }
    });
  }

  getDayName(day, short = false) {
    const date = new Date(day);
    let weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    if (short) {
      weekday = weekday.map(item => item.substring(0, 3));
    }
    const sufix = this.duplicatedWeekdays.includes(date.getDay())
      ? ` (${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth()).padStart(2, '0')})`
      : '';
    return weekday[date.getDay()] + sufix;
  }

  getDuplicatedWeekdays() {
    const weekdays = [];
    this.eventDays.forEach(day => {
      const weekday = new Date(day.day).getDay();
      if (weekdays.includes(weekday)) {
        this.duplicatedWeekdays.push(weekday);
      } else {
        weekdays.push(weekday);
      }
    });
  }

  preventNegative(event: KeyboardEvent): void {
    const invalidKeys = ['-', 'e', 'E', '+'];
    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  toNLLocalIso(d: Date): string {
    return this.datePipe.transform(d, 'yyyy-MM-ddTHH:mm', 'Europe/Amsterdam');
  }

  setPricingRules(formRow, event) {
    const selectEl = event.target as HTMLSelectElement;
    this.applyPricingRules(formRow, selectEl.value);
  }

  applyPricingRules(formRow, value: string) {
    const moneyAmount = formRow.get('money_amount');
    const serviceFee = formRow.get('service_fee');

    if (this.pricingRules) {
      const rules = this.pricingRules[value];

      // Amount
      if (rules.amount.enabled) {
        moneyAmount.enable();
        moneyAmount.setValidators([Validators.required, Validators.min(rules.amount.min)]);
      } else {
        moneyAmount.disable();
        moneyAmount.clearValidators();
        moneyAmount.setValue(null);
      }
      moneyAmount.updateValueAndValidity();

      // Fee
      if (rules.fee.enabled) {
        serviceFee.enable();
        serviceFee.setValidators([Validators.required, Validators.min(rules.fee.min)]);
      } else {
        serviceFee.disable();
        serviceFee.clearValidators();
        serviceFee.setValue(null);
      }
      serviceFee.updateValueAndValidity();
    }

    if (value === 'fully_discounted_product' || value === 'wingame_product') {
      moneyAmount.setValue(0);
    } else if (value === 'kpn') {
      moneyAmount.setValue(0);
      serviceFee.setValue(0);
    } else {
      if (moneyAmount.value === 0) {
        moneyAmount.setValue(null);
      }
    }

    const sealed = this.productsSealedByDefault.includes(formRow.get('product_sub_type').value);
    formRow.get('is_sealed').setValue(sealed);
  }

  submitForm() {
    if (this.form.valid) {
      const form = this.form.getRawValue();

      const crReq = {
        event_id: form.relation.id,
        products_data: form.products.map(p => ({
          title: p.title,
          product_sub_type: p.product_sub_type,
          customer_upload_min_instances_amount: p.customer_upload_min_instances_amount,
          days: p.days.map(d => d.id),
          customer_uploads_tickets: true,
          is_sealed: p.is_sealed,
          create_scan_results_task: p.create_scan_results_task,
          pricing: [
            {
              available_from: form.available_from,
              title: p.product_title,
              money_amount: p.money_amount,
              service_fee: p.service_fee,
            },
          ],
        })),
      };

      this.isSubmitting = true;
      if (this.taskManagement) {
        delete crReq.event_id;
        const payload = crReq.products_data;

        this.taskService.productsTaskAction(this.data.task.id, payload).subscribe(
          response => {
            this.dialogRef.close({
              success: true,
              info: 'Products have been created successfully',
            });
          },
          error => {
            this.isSubmitting = false;
            const translatedMsg = error.error.toString().startsWith('task_management.errors.')
              ? error.error
              : `internal error: ${error.error.toString().substring(0, 100)}`;
            this.layoutAlertService.error('Error when adding products: ' + translatedMsg);
          },
        );
      } else {
        this.shopProductService.createProducts(crReq).subscribe(
          data => {
            this.dialogRef.close({
              success: true,
              info: 'Products have beed created successfully',
            });
          },
          err => {
            this.isSubmitting = false;
            this.layoutAlertService.error('Error when adding products.');
          },
        );
      }
    }
  }

  getSortIcon(sortName: string) {
    const iconName = !this.showCreatedProducts ? 'arrow_downward' : 'arrow_upward';

    return `<i class="material-icons">${iconName}</i>`;
  }

  onDropdownOpened(index: number): void {
    if (index === this.products.length - 1) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    }
  }

  checkIfAddScroll() {
    const prods = this.products.controls.length;
    if (this.taskManagement) {
      if (prods > 4) {
        return true;
      } else {
        return false;
      }
    } else {
      if (prods > 5) {
        return true;
      } else {
        return false;
      }
    }
  }

  get productsMaxHeight(): string {
    if (this.showCreatedProducts) {
      const count = this.existingProducts?.length || 0;
      if (count >= 12) {
        return '300px';
      }
      const totalOffset = 470 + count * 20 + 40;
      return `calc(100vh - (${totalOffset}px))`;
    } else {
      return `calc(100vh - 470px)`;
    }
  }

  get crProductsMaxHeight(): string {
    if (this.showCreatedProducts) {
      const prodsMaxHeight = this.productsMaxHeight;
      const maxHeight = `calc(100vh - (${prodsMaxHeight} + 500px))`;
      return maxHeight;
    } else {
      return `0`;
    }
  }

  get products(): FormArray {
    return this.form.get('products') as FormArray;
  }

  get relation(): FormControl {
    return this.form.get('relation') as FormControl;
  }

  closeDialog() {
    this.dialogRef.close(false);
  }

  checkForm() {
    console.log('form', this.form.value);
    console.log('form', this.form);
  }
}

export interface PricingRules {
  regular_product: DiscountedProduct;
  kpn: DiscountedProduct;
  discounted_product: DiscountedProduct;
  fully_discounted_product: DiscountedProduct;
  wingame_product: DiscountedProduct;
}

export interface DiscountedProduct {
  amount: Amount;
  fee: Amount;
}

export interface Amount {
  min: number;
  enabled: boolean;
}
